/* Hawkstetler — scroll reveals + header state. No dependencies. */
(() => {
  const items = document.querySelectorAll('[data-hk-reveal]');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }),
      { rootMargin: '0px 0px -10% 0px' }
    );
    items.forEach((el) => io.observe(el));
  } else {
    items.forEach((el) => el.classList.add('is-in'));
  }

  const header = document.querySelector('[data-hk-header]');
  if (header && header.hasAttribute('data-overlay')) {
    const update = () => header.classList.toggle('is-solid', window.scrollY > 24);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  // Phone menu — Vitalenta's curtain: a curved dark wipe rises from the bottom, the menu is
  // swapped in while the screen is covered, then the curtain lifts off the top. Closing runs the
  // same wipe back from the top. Links navigate under the curtain.
  const menu = document.querySelector('[data-hk-menu]');
  const toggle = document.querySelector('[data-hk-menu-toggle]');
  const curtain = document.querySelector('.hk-curtain');
  if (menu && toggle && curtain) {
    const path = curtain.querySelector('path');
    const closeBtn = menu.querySelector('[data-hk-menu-close]');
    const D = {
      fromBottom: { open: 'M 0 100 V 100 Q 50 100 100 100 V 100 z', curve: 'M 0 100 V 50 Q 50 0 100 50 V 100 z', back: 'M 0 100 V 50 Q 50 100 100 50 V 100 z', full: 'M 0 100 V 0 Q 50 0 100 0 V 100 z' },
      fromTop: { full: 'M 0 0 V 100 Q 50 100 100 100 V 0 z', curve: 'M 0 0 V 50 Q 50 0 100 50 V 0 z', back: 'M 0 0 V 50 Q 50 100 100 50 V 0 z', gone: 'M 0 0 V 0 Q 50 0 100 0 V 0 z' },
    };
    let open = false;
    let busy = false;
    const calm = () => !window.gsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const apply = (state) => {
      open = state;
      menu.classList.toggle('is-open', state);
      toggle.classList.toggle('is-open', state);
      toggle.setAttribute('aria-expanded', String(state));
      menu.setAttribute('aria-hidden', String(!state));
      menu.toggleAttribute('inert', !state);
      document.documentElement.style.overflow = state ? 'hidden' : '';
      if (window.hkLenis) state ? window.hkLenis.stop() : window.hkLenis.start();
    };

    // cover: wipe up from the bottom until the screen is filled, then run `then`
    const cover = (gsap, then) => gsap.timeline()
      .set(path, { attr: { d: D.fromBottom.open } })
      .to(path, { duration: 0.65, ease: 'power4.in', attr: { d: D.fromBottom.curve } })
      .to(path, { duration: 0.18, ease: 'power1', attr: { d: D.fromBottom.full }, onComplete: then });

    const play = (state) => {
      if (busy || state === open) return;
      if (calm()) { apply(state); (state ? closeBtn : toggle)?.focus({ preventScroll: true }); return; }
      const { gsap } = window;
      busy = true;
      curtain.classList.add('is-playing');
      const tl = gsap.timeline({
        onComplete: () => {
          busy = false;
          curtain.classList.remove('is-playing');
          (state ? closeBtn : toggle)?.focus({ preventScroll: true });
        },
      });
      if (state) {
        tl.add(cover(gsap, () => apply(true)))
          .set(path, { attr: { d: D.fromTop.full } })
          .to(path, { duration: 0.18, ease: 'sine.in', attr: { d: D.fromTop.curve } })
          .to(path, { duration: 0.82, ease: 'power4', attr: { d: D.fromTop.gone } });
      } else {
        tl.set(path, { attr: { d: D.fromTop.gone } })
          .to(path, { duration: 0.65, ease: 'power4.in', attr: { d: D.fromTop.back } })
          .to(path, { duration: 0.18, ease: 'power1', attr: { d: D.fromTop.full }, onComplete: () => apply(false) })
          .set(path, { attr: { d: D.fromBottom.full } })
          .to(path, { duration: 0.18, ease: 'sine.in', attr: { d: D.fromBottom.back } })
          .to(path, { duration: 0.82, ease: 'power4', attr: { d: D.fromBottom.open } });
      }
    };

    toggle.addEventListener('click', () => play(true));
    closeBtn && closeBtn.addEventListener('click', () => play(false));

    // A link in the open menu: cover the screen again, then leave under the curtain.
    menu.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || busy) return;
      const url = new URL(link.href, location.href);
      const external = url.origin !== location.origin || link.target === '_blank';
      const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (external || modified || link.href.startsWith('mailto:')) return;
      event.preventDefault();
      if (calm()) { location.href = url.href; return; }
      busy = true;
      curtain.classList.add('is-playing');
      cover(window.gsap, () => { location.href = url.href; });
    });

    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && open) play(false); });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => { if (event.matches && open) apply(false); });
    // Coming back with the browser's back button restores the page as it was left — reset it.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      busy = false;
      curtain.classList.remove('is-playing');
      if (open) apply(false);
    });
  }

  // Collector list popup (sections/hk-collector-modal.liquid). Any link to #collector-list opens it.
  const modal = document.querySelector('[data-hk-modal]');
  if (modal && typeof modal.showModal === 'function') {
    const OPENER = 'a[href$="#collector-list"], [data-hk-modal-open]';
    let lastFocus = null;
    const open = () => {
      if (modal.open) return;
      lastFocus = document.activeElement;
      modal.classList.remove('is-closing');
      modal.showModal();
      document.documentElement.style.overflow = 'hidden';
      if (window.hkLenis) window.hkLenis.stop();
      const field = modal.querySelector('input[type="email"]');
      if (field && !window.matchMedia('(hover: none)').matches) field.focus({ preventScroll: true });
    };
    const close = () => {
      if (!modal.open || modal.classList.contains('is-closing')) return;
      const done = () => {
        modal.classList.remove('is-closing');
        modal.close();
        document.documentElement.style.overflow = '';
        if (window.hkLenis) window.hkLenis.start();
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
      modal.classList.add('is-closing');
      modal.addEventListener('animationend', done, { once: true });
    };
    // capture phase, so the smooth-scroll handler for in-page links never sees these clicks
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(OPENER);
      if (!trigger) return;
      const url = trigger.href && new URL(trigger.href, location.href);
      if (url && url.pathname !== location.pathname && trigger.matches('a[href^="/"], a[href^="http"]')) return; // another page's anchor
      event.preventDefault();
      event.stopPropagation();
      open();
    }, true);
    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-hk-modal-close]') || event.target === modal) close();
    });
    modal.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    // after a sign-up Shopify reloads the page: show the thank-you (or the error) straight away
    if (modal.hasAttribute('data-open-on-load')) {
      if (location.hash === '#CollectorModalForm') history.replaceState(null, '', location.pathname + location.search);
      open();
    }
    // theme editor: selecting the section opens it, leaving it closes it
    document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === modal.dataset.sectionId) open(); });
    document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === modal.dataset.sectionId) close(); });
  }

  // Gallery wall: place items left to right, each into the shortest column (heights from aspect ratio).
  document.querySelectorAll('[data-hk-masonry]').forEach((grid) => {
    const items = Array.from(grid.children);
    let built = 0;
    const layout = () => {
      const n = parseInt(getComputedStyle(grid).getPropertyValue('--cols'), 10) || 2;
      if (n === built) return;
      built = n;
      grid.classList.remove('is-masonry');
      grid.querySelectorAll('.hk-gallery__col').forEach((c) => c.remove());
      const cols = Array.from({ length: n }, () => {
        const col = document.createElement('li');
        col.className = 'hk-gallery__col';
        col.setAttribute('role', 'none');
        const list = document.createElement('ul');
        list.setAttribute('role', 'list');
        list.style.cssText = 'list-style:none;margin:0;padding:0';
        col.appendChild(list);
        grid.appendChild(col);
        return { list, h: 0 };
      });
      items.forEach((item) => {
        const ratio = parseFloat(item.dataset.ratio) || 1;
        const col = cols.reduce((a, b) => (b.h < a.h - 0.001 ? b : a));
        col.list.appendChild(item);
        col.h += 1 / ratio + 0.05; // image height in column widths, plus the gap
      });
      grid.classList.add('is-masonry');
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    };
    layout();
    window.addEventListener('resize', layout);
  });

  // Gallery lightbox (sections/hk-gallery.liquid): full-size preview, prev/next, swipe, keys.
  document.querySelectorAll('[data-hk-gallery]').forEach((wall) => {
    const light = wall.querySelector('[data-hk-gallery-light]');
    if (!light || typeof light.showModal !== 'function') return;
    const tiles = () => Array.from(wall.querySelectorAll('.hk-gallery__tile'))
      .sort((a, b) => (+a.dataset.order || 0) - (+b.dataset.order || 0));
    const img = light.querySelector('[data-hk-gallery-img]');
    const title = light.querySelector('[data-hk-gallery-title]');
    const meta = light.querySelector('[data-hk-gallery-meta]');
    const status = light.querySelector('[data-hk-gallery-status]');
    const link = light.querySelector('[data-hk-gallery-link]');
    const count = light.querySelector('[data-hk-gallery-count]');
    let index = 0;
    let opener = null;

    const show = (i) => {
      const list = tiles();
      if (!list.length) return;
      index = (i + list.length) % list.length;
      const t = list[index].dataset;
      img.classList.add('is-loading');
      img.onload = () => img.classList.remove('is-loading');
      img.src = t.full;
      img.alt = t.title || '';
      title.textContent = t.title || '';
      meta.textContent = t.meta || '';
      status.textContent = t.status || '';
      if (count) count.textContent = (index + 1) + ' / ' + list.length;
      // the button keeps its place when a work has no page, so nothing below it moves between images
      if (t.url) link.href = t.url;
      link.classList.toggle('is-off', !t.url);
      link.toggleAttribute('aria-hidden', !t.url);
      link.tabIndex = t.url ? 0 : -1;
      // warm the neighbours so arrows feel instant
      [index - 1, index + 1].forEach((n) => { const d = list[(n + list.length) % list.length]; if (d) new Image().src = d.dataset.full; });
    };
    const open = (i, from) => {
      opener = from || null;
      show(i);
      light.classList.remove('is-closing');
      if (!light.open) light.showModal();
      document.documentElement.style.overflow = 'hidden';
      if (window.hkLenis) window.hkLenis.stop();
    };
    const close = () => {
      if (!light.open || light.classList.contains('is-closing')) return;
      const done = () => {
        light.classList.remove('is-closing');
        light.close();
        document.documentElement.style.overflow = '';
        if (window.hkLenis) window.hkLenis.start();
        if (opener) opener.focus({ preventScroll: true });
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
      light.classList.add('is-closing');
      light.addEventListener('animationend', done, { once: true });
    };

    wall.addEventListener('click', (event) => {
      const tile = event.target.closest('.hk-gallery__tile');
      if (tile) open(tiles().indexOf(tile), tile);
    });
    light.addEventListener('click', (event) => {
      if (event.target.closest('[data-hk-gallery-close]') || event.target === light) close();
      else if (event.target.closest('[data-hk-gallery-prev]')) show(index - 1);
      else if (event.target.closest('[data-hk-gallery-next]')) show(index + 1);
    });
    light.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    light.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });
    // swipe left / right on phones
    let x0 = null;
    light.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    light.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
      x0 = null;
    });
  });

  // Contact form: ?topic=commission and ?work=<artwork title> pre-fill it.
  const contactForm = document.getElementById('ContactForm');
  if (contactForm) {
    const params = new URLSearchParams(location.search);
    const handle = (v) => v.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const topic = params.get('topic');
    if (topic) {
      const radio = contactForm.querySelector(`[data-topic="${handle(topic)}"]`);
      if (radio) radio.checked = true;
    }
    const work = params.get('work');
    const workField = contactForm.querySelector('[data-hk-contact-work]');
    if (work && workField) {
      workField.hidden = false;
      workField.querySelector('input').value = work;
      if (!topic) {
        const original = contactForm.querySelector('[data-topic="an-original"]');
        if (original) original.checked = true;
      }
    }
  }

  // Shopify marks every contact form on a page as sent; the redirect's #hash names the one that was.
  if (/[?&](contact_posted|form_type)=/.test(location.search) || document.querySelector('[data-hk-posted]')) {
    document.querySelectorAll('[data-hk-posted-form]').forEach((form) => {
      if (location.hash === '#' + form.id) return;
      form.querySelectorAll('[data-hk-posted]').forEach((el) => el.remove());
      form.querySelectorAll('[data-hk-fields]').forEach((el) => { el.hidden = false; });
      form.querySelectorAll('[data-hk-posted-open]').forEach((el) => { el.open = false; });
    });
  }

  // Register interest (hk-product): Buy / Sell open the short form with that choice set.
  document.querySelectorAll('.hk-interest').forEach((form) => {
    const details = form.querySelector('[data-hk-interest-details]');
    const value = form.querySelector('[data-hk-interest-value]');
    const heading = form.querySelector('[data-hk-interest-heading]');
    const base = heading ? heading.textContent : '';
    const buttons = form.querySelectorAll('[data-hk-interest]');
    if (!details || !value) return;
    buttons.forEach((btn) => btn.addEventListener('click', () => {
      value.value = btn.dataset.hkInterest;
      buttons.forEach((b) => {
        b.setAttribute('aria-pressed', String(b === btn));
        b.setAttribute('aria-expanded', String(b === btn));
      });
      if (heading) heading.textContent = 'You would like to ' + btn.textContent.trim().toLowerCase() + ' this work. ' + base;
      details.setAttribute('data-open', '');
      const first = details.querySelector('input[type="text"]');
      if (first) first.focus({ preventScroll: true });
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }));
  });

  // Countdowns (snippets/hk-countdown.liquid): tick every second; at zero the page reloads so the
  // release shows its new state (opened / closed).
  const timers = document.querySelectorAll('[data-hk-countdown]');
  if (timers.length) {
    const pad = (n) => String(n).padStart(2, '0');
    let reloading = false;
    const tick = () => {
      const now = Date.now() / 1000;
      timers.forEach((el) => {
        const left = Math.max(0, Math.floor(+el.dataset.hkCountdown - now));
        const set = (k, v) => { const n = el.querySelector(`[data-${k}]`); if (n) n.textContent = v; };
        const compact = el.classList.contains('hk-countdown--compact');
        const d = Math.floor(left / 86400);
        set('d', compact ? d : pad(d));
        set('h', pad(Math.floor((left % 86400) / 3600)));
        set('m', pad(Math.floor((left % 3600) / 60)));
        set('s', pad(left % 60));
        if (left === 0 && !reloading && now - (+el.dataset.hkCountdown) < 5) {
          reloading = true;
          setTimeout(() => location.reload(), 1500);
        }
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  // Product gallery: thumbnails swap the main image.
  document.querySelectorAll('[data-hk-pgallery]').forEach((gallery) => {
    // Shopify drops empty attributes from image_tag, so fall back to the main image itself
    const main = gallery.querySelector('[data-hk-pmain], .hk-product__main img');
    const thumbs = gallery.querySelectorAll('.hk-product__thumb');
    if (!main || !thumbs.length) return;
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      main.removeAttribute('srcset');
      main.srcset = thumb.dataset.srcset;
      main.src = thumb.dataset.src;
      main.alt = thumb.dataset.alt;
      thumbs.forEach((t) => t.setAttribute('aria-pressed', String(t === thumb)));
    }));
  });

  // Register interest (sections/hk-register-interest.liquid): ?i=buying|selling and ?work= fill
  // step one; Continue checks it and shows step two.
  const interest = document.querySelector('[data-hk-register]');
  if (interest) {
    const params = new URLSearchParams(location.search);
    const kind = params.get('i');
    const work = params.get('work');
    const radio = kind && interest.querySelector(`input[name="contact[Interest]"][value="${kind === 'selling' ? 'Selling' : 'Buying'}"]`);
    if (radio) radio.checked = true;
    const note = interest.querySelector('textarea');
    const artist = interest.dataset.artist;
    if (work) {
      const field = interest.querySelector('[data-hk-register-work]');
      if (field) field.value = work;
      if (note && !note.value) note.value = `Re: ${artist}, ${work}`;
    }
    const steps = interest.querySelectorAll('[data-hk-step]');
    const dots = interest.querySelectorAll('[data-hk-step-dot]');
    const go = (n) => {
      steps.forEach((step, i) => { step.hidden = i !== n; });
      dots.forEach((dot, i) => dot.classList.toggle('is-done', i <= n));
      const first = steps[n] && steps[n].querySelector('input:not([type=hidden]):not([type=radio]), textarea');
      if (first) first.focus({ preventScroll: true });
    };
    if (steps.length > 1 && !interest.querySelector('[data-hk-register-errors]')) go(0);
    interest.querySelectorAll('[data-hk-step-next]').forEach((btn) => btn.addEventListener('click', () => {
      if (note && !note.reportValidity()) return;
      go(1);
    }));
    interest.querySelectorAll('[data-hk-step-back]').forEach((btn) => btn.addEventListener('click', () => go(0)));
    if (note) {
      const counter = interest.querySelector('[data-hk-word-count]');
      const count = () => { if (counter) counter.textContent = (note.value.trim() ? note.value.trim().split(/\s+/).length : 0) + '/500'; };
      note.addEventListener('input', count);
      count();
    }
  }

  // Swipe rows: keep the position dots in step with the scroll.
  document.querySelectorAll('[data-hk-swipe]').forEach((row) => {
    const dots = row.parentElement.querySelectorAll('.hk-swipe__dots span');
    if (!dots.length) return;
    const update = () => {
      const card = row.firstElementChild;
      if (!card) return;
      const step = card.getBoundingClientRect().width + 16;
      const max = row.scrollWidth - row.clientWidth;
      const i = row.scrollLeft >= max - 4 ? dots.length - 1 : Math.round(row.scrollLeft / step);
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    };
    row.addEventListener('scroll', update, { passive: true });
    update();
  });
})();
